/**
 * Admin Sales — `/api/admin/v2/sales`.
 *
 * Bodies are camelCase. Acting-as is `?acting_as=` (honoured only with
 * `sales.act_as`; otherwise ignored, never 400).
 */
import { useCallback } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  AcquisitionStage,
  Availability,
  ClaimStatus,
  ClaimWin,
  LossReason,
  OutreachChannel,
  OutreachFeedbackOutcome,
  OutreachFeedbackSentiment,
  OutreachMode,
  PerformancePeriod,
  SalesClaim,
  SalesConversation,
  SalesConversationChannel,
  SalesConversationThread,
  SalesFloorSettings,
  SalesRepSettings,
  SalesSettingsOverview,
  SalesSubjectSettings,
  SalesDashboard,
  SalesEvent,
  SalesMe,
  SalesOffer,
  SalesOutreach,
  SalesPolicy,
  SalesPurpose,
  SalesRep,
  SalesRepRole,
  SalesSearchPage,
  SalesImportSkip,
  SalesRegionSuggestion,
  SalesTagCount,
  SalesSearchPresence,
  SalesSearchSort,
  SalesSearchTier,
  SalesSellerKind,
  SalesSelfCheck,
  SalesShare,
  SalesSubject,
  SalesVertical,
  SourceKind,
  SpendBand,
  SubjectKind,
} from '@/client/types/sales';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { getApiErrorCode } from '@/client/utils/errors';
import { SALES_BASE, salesKeys } from './keys';

export const SALES_ACTING_STORAGE_KEY = 'ff-admin.sales.currentRepId.v2';
export const SALES_NO_ONE = 'none';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export function readActingAsRepId(): string {
  try {
    return sessionStorage.getItem(SALES_ACTING_STORAGE_KEY) || SALES_NO_ONE;
  } catch {
    return SALES_NO_ONE;
  }
}

function withActingAs(path: string): string {
  const acting = readActingAsRepId();
  if (!acting || acting === SALES_NO_ONE) return `${SALES_BASE}${path}`;
  const sep = path.includes('?') ? '&' : '?';
  return `${SALES_BASE}${path}${sep}acting_as=${encodeURIComponent(acting)}`;
}

function normalizeSubject(raw: SalesSubject): SalesSubject {
  return {
    ...raw,
    displayName: raw.displayName || 'Prospect',
    kind: raw.kind || 'person',
    matchKeys: raw.matchKeys ?? [],
    areas: raw.areas ?? [],
    verticals: raw.verticals ?? [],
    spendBand: raw.spendBand ?? 'unknown',
    fitParts: raw.fitParts ?? { areaDemand: 0.25, activityRecency: 0.25, spendBand: 0.25 },
    activity: raw.activity ?? { listingCount: 0 },
    source: raw.source ?? { system: 'manual' },
    consent: raw.consent ?? {},
    doNotContact: Boolean(raw.doNotContact),
    goalCooldownUntil: raw.goalCooldownUntil ?? {},
    createdAt: raw.createdAt || new Date().toISOString(),
    acquisitionStage: raw.acquisitionStage || 'prospect',
    profile: raw.profile ?? { autoFilled: [], manualOverrides: [] },
    tags: raw.tags ?? [],
  };
}

function normalizeOutreach(raw: SalesOutreach): SalesOutreach {
  return { ...raw, trackedLinks: raw.trackedLinks ?? [] };
}

function asOutreachPage(data: unknown): { outreach: SalesOutreach[]; total: number } {
  const bag = data && typeof data === 'object' ? (data as { outreach?: SalesOutreach[]; items?: SalesOutreach[]; total?: number }) : null;
  const rows = Array.isArray(data) ? data : (bag?.outreach ?? bag?.items ?? []);
  return {
    outreach: rows.map(normalizeOutreach),
    total: bag?.total ?? rows.length,
  };
}

const AVATARS = [
  'bg-slate-700',
  'bg-teal-700',
  'bg-indigo-700',
  'bg-amber-700',
  'bg-rose-700',
  'bg-emerald-700',
];

export function decorateRep(raw: SalesRep | null | undefined): SalesRep | null {
  if (!raw) return null;
  const id = raw.id || '';
  const hue = id.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return {
    ...raw,
    name: raw.name || 'Rep',
    role: raw.role || 'agent',
    desk: raw.desk || 'all',
    title: raw.title || '',
    territory: raw.territory ?? { regions: [], verticals: [] },
    avatarColor: raw.avatarColor || AVATARS[hue % AVATARS.length],
  };
}

export function useSalesApi() {
  const request = useClientStore((s) => s.request);
  return useCallback(
    async <T,>(path: string, method: HttpMethod = 'GET', body?: unknown): Promise<T> => {
      const raw = await request<AdminEnvelope<T> | T>(withActingAs(path), method, body);
      if (raw && typeof raw === 'object' && 'data' in raw) return (raw as AdminEnvelope<T>).data;
      return raw as T;
    },
    [request],
  );
}

export const useSalesMeQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.me(),
    queryFn: () => api<SalesMe>('/me'),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSalesRepsQuery = (includeInactive = false) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.reps(includeInactive),
    queryFn: async () => {
      const data = await api<{ reps: SalesRep[] }>(
        includeInactive ? '/reps?include_inactive=1' : '/reps',
      );
      return (data.reps ?? []).map((r) => decorateRep(r)!);
    },
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSalesPolicyQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.policy(),
    queryFn: () => api<SalesPolicy>('/policy'),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSalesPurposesQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.purposes(),
    queryFn: async () => {
      const data = await api<{ purposes: SalesPurpose[] }>('/purposes');
      return data.purposes ?? [];
    },
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSalesOffersQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.offers(),
    queryFn: async () => {
      const data = await api<{ offers: SalesOffer[] }>('/offers');
      return data.offers ?? [];
    },
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSalesTeamQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.team(),
    queryFn: () => api<{ members: SalesRep[]; byLevel: Record<string, unknown> }>('/team'),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSalesSelfCheckQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.selfCheck(),
    queryFn: () => api<SalesSelfCheck>('/self-check'),
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export type UpsertRepInput = {
  uid: number;
  role?: SalesRepRole;
  desk?: SalesVertical | 'all';
  title?: string;
  quotaGhs?: number;
  name?: string;
  email?: string;
  active?: boolean;
};

export const useUpsertRepMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertRepInput) => api<SalesRep>('/reps', 'POST', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const usePatchRepMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<UpsertRepInput>) =>
      api<SalesRep>(`/reps/${id}`, 'PATCH', patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const usePatchPolicyMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<SalesPolicy>) => api<SalesPolicy>('/policy', 'PATCH', patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.policy() });
      void qc.invalidateQueries({ queryKey: salesKeys.selfCheck() });
    },
  });
};

export const useUpsertPurposeMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<SalesPurpose> & { id?: string }) => {
      const { id, ...body } = input;
      return id
        ? api<SalesPurpose>(`/purposes/${id}`, 'PATCH', body)
        : api<SalesPurpose>('/purposes', 'POST', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.purposes() });
      void qc.invalidateQueries({ queryKey: salesKeys.selfCheck() });
    },
  });
};

export const useUpsertOfferMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<SalesOffer> & { id?: string }) => {
      const { id, ...body } = input;
      return id
        ? api<SalesOffer>(`/offers/${id}`, 'PATCH', body)
        : api<SalesOffer>('/offers', 'POST', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.offers() });
      void qc.invalidateQueries({ queryKey: salesKeys.selfCheck() });
    },
  });
};

export const useSalesSweepMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ swept?: number }>('/sweep', 'POST', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '' || v === 'all' || v === false) continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export type SalesSubjectsParams = {
  kind?: SubjectKind | 'all';
  spend?: SpendBand | 'all';
  vertical?: SalesVertical | 'all';
  stage?: AcquisitionStage | 'all';
  origin?: SourceKind | 'all';
  presence?: 'all' | 'on_platform' | 'to_onboard';
  availability?: Availability | 'all';
  q?: string;
  region?: string;
  tag?: string;
  limit?: number;
  offset?: number;
};

/** Tags in use across the pool, most used first. */
export const useSalesTagsQuery = () => {
  const api = useSalesApi();
  return useQuery({
    queryKey: [...salesKeys.all, 'tags'],
    queryFn: async () => (await api<{ tags?: SalesTagCount[] }>('/subjects/tags')).tags ?? [],
    staleTime: 60_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Region values from the marketplace index, most sellers first. */
export const useSalesRegionsQuery = (q: string) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: [...salesKeys.all, 'regions', q],
    queryFn: async () =>
      (await api<{ items?: SalesRegionSuggestion[] }>(`/search/regions${qs({ q, limit: 15 })}`)).items ?? [],
    staleTime: 5 * 60_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSalesSubjectsQuery = (params: SalesSubjectsParams = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.subjects(params),
    queryFn: async () => {
      const data = await api<{ subjects: SalesSubject[]; total: number }>(`/subjects${qs(params)}`);
      return {
        subjects: (data.subjects ?? []).map(normalizeSubject),
        total: data.total ?? 0,
      };
    },
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSalesSubjectQuery = (id: string | undefined) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.subjects({ id }),
    queryFn: async () => normalizeSubject(await api<SalesSubject>(`/subjects/${id}`)),
    enabled: Boolean(id),
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSalesSubjectThreadQuery = (id: string | undefined) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: [...salesKeys.subjects({ id }), 'thread'],
    queryFn: async () => {
      const data = await api<{ events?: SalesEvent[]; outreach?: SalesOutreach[] }>(`/subjects/${id}/thread`);
      return {
        events: data.events ?? [],
        outreach: (data.outreach ?? []).map(normalizeOutreach),
      };
    },
    enabled: Boolean(id),
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export type SalesClaimsParams = {
  status?: ClaimStatus | 'all';
  ownerId?: string;
  limit?: number;
  offset?: number;
};

export const useSalesClaimsQuery = (params: SalesClaimsParams = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.claims(params),
    queryFn: async () => {
      const data = await api<{ claims: SalesClaim[]; total: number }>(`/claims${qs(params)}`);
      return { claims: data.claims ?? [], total: data.total ?? 0 };
    },
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export type SalesOutreachParams = {
  tab?: 'due' | 'drafts' | 'sent' | 'all';
  limit?: number;
  offset?: number;
};

export const useSalesOutreachQuery = (params: SalesOutreachParams = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.outreach(params),
    queryFn: async () => {
      const tab = params.tab === 'all' ? undefined : params.tab;
      const data = await api<unknown>(`/outreach${qs({ tab, limit: params.limit, offset: params.offset })}`);
      return asOutreachPage(data);
    },
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export type SalesDashboardParams = {
  period?: PerformancePeriod;
  compare?: 'prior' | 'off';
  scope?: 'floor' | 'me';
  goal?: string;
};

export const useSalesDashboardQuery = (params: SalesDashboardParams = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.dashboard(params),
    queryFn: () => api<SalesDashboard>(`/dashboard${qs(params)}`),
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useCreateSubjectMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api<SalesSubject>('/subjects', 'POST', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useImportSubjectsMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (csv: string) =>
      api<{ imported?: SalesSubject[]; skipped?: SalesImportSkip[]; importedCount?: number; skippedCount?: number }>(
        '/subjects/import',
        'POST',
        { csv },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const usePatchSubjectMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      api<SalesSubject>(`/subjects/${id}`, 'PATCH', patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useClaimSubjectMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subjectId, purposeId, offerId }: { subjectId: string; purposeId: string; offerId?: string }) =>
      api<SalesClaim>(`/subjects/${subjectId}/claim`, 'POST', { purposeId, offerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

/** Manager override: end a prospect's cooldown so it can be claimed again. */
export const useClearCooldownMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subjectId: string) => api<SalesSubject>(`/subjects/${subjectId}/cooldown`, 'DELETE'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

function useClaimAction(path: string) {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
      api<SalesClaim>(`/claims/${id}/${path}`, 'POST', body ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
}

export const usePursueClaimMutation = () => useClaimAction('pursue');
export const useCommitClaimMutation = () => useClaimAction('commit');
export const useAdvanceClaimMutation = () => useClaimAction('advance');
export const useReleaseClaimMutation = () => useClaimAction('release');

export const useWinClaimMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, evidence }: { id: string; evidence: Omit<ClaimWin, 'at' | 'goal'> }) =>
      api<SalesClaim>(`/claims/${id}/win`, 'POST', evidence),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useLoseClaimMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, note }: { id: string; reason: LossReason; note?: string }) =>
      api<SalesClaim>(`/claims/${id}/lose`, 'POST', { reason, note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useAttachOfferMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, offerId }: { id: string; offerId: string }) =>
      api<SalesClaim>(`/claims/${id}/offer`, 'POST', { offerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useReassignClaimMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ownerId }: { id: string; ownerId: string }) =>
      api<SalesClaim>(`/claims/${id}/reassign`, 'POST', { ownerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useComposeOutreachMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subjectId: string;
      channel: OutreachChannel;
      body: string;
      mode?: OutreachMode;
      templateId?: string;
      dest?: string;
      nextStepDue?: string;
    }) => api<SalesOutreach>('/outreach', 'POST', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useSendOutreachMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nextStepDue }: { id: string; nextStepDue?: string }) =>
      api<SalesOutreach>(`/outreach/${id}/send`, 'POST', { nextStepDue }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

/** The Sales settings page: floor switches, senders, quiet hours, every rep's and prospect's overrides. */
export const useSalesSettingsQuery = (enabled = true) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: [...salesKeys.all, 'settings'],
    queryFn: () => api<SalesSettingsOverview>('/settings'),
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** A PATCH sends only the keys to change; `null` removes an override. */
type Patch<T> = { [K in keyof T]?: T[K] | null };

export const usePatchFloorSettingsMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Patch<SalesFloorSettings>) => api('/settings', 'PATCH', patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.all }),
  });
};

export const usePatchRepSettingsMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ repId, patch }: { repId: string; patch: Patch<SalesRepSettings> }) =>
      api(`/reps/${repId}/settings`, 'PATCH', patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.all }),
  });
};

export const usePatchSubjectSettingsMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subjectId, patch }: { subjectId: string; patch: Patch<SalesSubjectSettings> }) =>
      api(`/subjects/${subjectId}/settings`, 'PATCH', patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: salesKeys.all }),
  });
};

/** A prospect in the inbox's scope with no messages yet, offered to start a conversation. */
export type SalesUnwritten = { id: string; displayName: string; phone?: string | null; email?: string | null };

/** The Sales inbox: prospects' messages on WhatsApp, SMS and email. Polls like the Communications inbox. */
export const useSalesConversationsQuery = (params: { channel?: string; mailbox?: string; search?: string } = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: [...salesKeys.all, 'conversations', params],
    queryFn: async () => {
      const data = await api<{ conversations?: SalesConversation[]; unwritten?: SalesUnwritten[] }>(`/conversations${qs(params)}`);
      return { conversations: data.conversations ?? [], unwritten: data.unwritten ?? [] };
    },
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSalesConversationQuery = (subjectId: string | undefined) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: [...salesKeys.all, 'conversation', subjectId],
    queryFn: () => api<SalesConversationThread>(`/conversations/${subjectId}`),
    enabled: Boolean(subjectId),
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Ask again whether the prospect's number is on WhatsApp (one lookup). */
export const useSalesWhatsAppCheckMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subjectId: string) => api(`/conversations/${subjectId}/whatsapp-check`, 'POST', {}),
    onSuccess: (_d, subjectId) => void qc.invalidateQueries({ queryKey: [...salesKeys.all, 'conversation', subjectId] }),
  });
};

export const useSalesConversationReadMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subjectId: string) => api<{ success: boolean }>(`/conversations/${subjectId}/read`, 'POST', {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...salesKeys.all, 'conversations'] }),
  });
};

/** Compose and send in one step; every outreach guard applies and a refusal names its rule. */
export const useSalesConversationSendMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subjectId,
      ...input
    }: {
      subjectId: string;
      channel: SalesConversationChannel;
      body: string;
      subject?: string;
      html?: string;
      /** One of the thread's `recipients`; the main line when left out. */
      to?: string;
      /** An inbox mailbox (email); the floor's Sales mailbox when left out. */
      from?: string;
      signature?: boolean;
    }) => api<SalesOutreach>(`/conversations/${subjectId}/send`, 'POST', input),
    onSettled: () => void qc.invalidateQueries({ queryKey: salesKeys.all }),
  });
};

export const useLogOutreachMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subjectId: string;
      channel: OutreachChannel;
      body: string;
      direction?: 'outbound' | 'inbound';
      nextStepDue?: string;
      templateId?: string;
    }) => api<SalesOutreach>('/outreach/log', 'POST', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export const useOutreachFeedbackMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      sentiment,
      outcome,
      note,
    }: {
      id: string;
      sentiment: OutreachFeedbackSentiment;
      outcome: OutreachFeedbackOutcome;
      note?: string;
    }) => api<SalesOutreach>(`/outreach/${id}/feedback`, 'POST', { sentiment, outcome, note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
    },
  });
};

export type SalesSearchParams = {
  vertical?: SalesVertical | 'all';
  region?: string;
  q?: string;
  tier?: SalesSearchTier | 'all';
  hasPhone?: boolean;
  notInPool?: boolean;
  recencyDays?: number | 'all';
  presence?: SalesSearchPresence | 'all';
  sellerKind?: SalesSellerKind | 'all';
  minInventory?: number;
  maxInventory?: number;
  minListings?: number;
  minPosted30?: number;
  sort?: SalesSearchSort;
  limit?: number;
  offset?: number;
};

export const useSalesSearchQuery = (params: SalesSearchParams = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.search(params),
    queryFn: async () => {
      const data = await api<SalesSearchPage>(
        `/search${qs({
          vertical: params.vertical,
          region: params.region,
          q: params.q,
          tier: params.tier,
          hasPhone: params.hasPhone,
          notInPool: params.notInPool,
          recencyDays: params.recencyDays,
          presence: params.presence,
          sellerKind: params.sellerKind,
          minInventory: params.minInventory,
          maxInventory: params.maxInventory,
          minListings: params.minListings,
          minPosted30: params.minPosted30,
          sort: params.sort ?? 'value',
          limit: params.limit ?? 20,
          offset: params.offset ?? 0,
        })}`,
      );
      return {
        items: data.items ?? [],
        total: data.total ?? 0,
        asOf: data.asOf ?? null,
        note: data.note,
      };
    },
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    retry: (count, err) => getApiErrorCode(err) !== 'upstream_forbidden' && count < 2,
  });
};

export type SalesShareParams = {
  period?: PerformancePeriod;
  vertical?: SalesVertical | 'all';
};

export const useSalesShareQuery = (params: SalesShareParams = {}) => {
  const api = useSalesApi();
  return useQuery({
    queryKey: salesKeys.share(params),
    queryFn: () => {
      const period = params.period ?? '30d';
      const extra = qs({ vertical: params.vertical });
      const sep = extra ? '&' : '?';
      return api<SalesShare>(`/share${extra}${sep}period=${encodeURIComponent(period)}`);
    },
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    retry: (count, err) => getApiErrorCode(err) !== 'upstream_forbidden' && count < 2,
  });
};

export const usePoolSearchHitMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ hitId, confirmNew }: { hitId: string; confirmNew?: boolean }) =>
      api<SalesSubject>(`/search/${hitId}/pool`, 'POST', confirmNew ? { confirmNew: true } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...salesKeys.all, 'search'] });
      void qc.invalidateQueries({ queryKey: [...salesKeys.all, 'subjects'] });
    },
  });
};

export const useSkipSearchHitMutation = () => {
  const api = useSalesApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hitId: string) => api<{ hitId: string; skippedUntil: string }>(`/search/${hitId}/skip`, 'POST', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...salesKeys.all, 'search'] });
    },
  });
};
